import type {
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from 'ag-grid-community';

import { canManageUser } from '@app/permissions/permissions.utils';
import { Role } from '@app/utils/protected-routes.utils';
import { UserRowActions } from '@features/user-management/components/UserRowActions';
import { DateColumnOnTwoLines, DisplayYesOrNo, UserRoles } from '@shared/components/ui/custom';

import type { User } from '../api/user-management.types';

export const userManagementColumns: ColDef[] = [
  {
    headerName: 'Email',
    field: 'email',
    flex: 2,
  },
  {
    headerName: 'First Name',
    field: 'firstName',
    flex: 1,
  },
  {
    headerName: 'Last Name',
    field: 'lastName',
    flex: 1,
  },
  {
    headerName: 'Date Created',
    field: 'creationDate',
    flex: 1,
    cellRenderer: (params: ICellRendererParams<User, string>) => {
      if (!params.value) return null;
      return <DateColumnOnTwoLines dateString={params.value} />;
    },
  },
  {
    headerName: 'Roles',
    field: 'roles',
    flex: 3,
    wrapText: true,
    autoHeight: true,
    valueFormatter: (params) => {
      const roles = Array.isArray(params.value) ? params.value : [];
      return roles.join(', ');
    },
    cellRenderer: (params: ICellRendererParams<User, Role[]>) => {
      const roles = Array.isArray(params.value) ? params.value : [];
      return <UserRoles roles={roles} />;
    },
  },
  {
    headerName: 'Locked',
    field: 'locked',
    flex: 0.5,
    cellRenderer: (params: ICellRendererParams<User, boolean>) => {
      return <DisplayYesOrNo value={!!params.value} />;
    },
  },
  {
    headerName: 'Actions',
    field: 'actions',
    width: 80,
    sortable: false,
    suppressSizeToFit: true,
    cellRenderer: (params: ICellRendererParams<User>) => {
      if (!params.data) return null;
      return <UserRowActions rowData={params.data} />;
    },
  },
];

export const getUserPermissions = (
  loggedInRoles: Role[],
  targetRoles: Role[],
  loggedInUsername: string,
  targetUsername: string,
): {
  canEdit: boolean;
  canEditSelf: boolean;
  canDeleteOrLock: boolean;
  canResetPassword: boolean;
} => {
  const isSelf = loggedInUsername === targetUsername;
  const isSuperAdmin = loggedInRoles.includes(Role.SUPER_ADMIN);
  const isAdmin = loggedInRoles.includes(Role.ADMIN);

  // Self-editing permissions: Admin and Super Admin can edit themselves
  const canEditSelf = isSelf && (isAdmin || isSuperAdmin);

  // Use the new permission system to check if user can manage the target user
  const canEditOthers = !isSelf && canManageUser(loggedInRoles, targetRoles);

  const canEdit = canEditSelf || canEditOthers;
  const canDeleteOrLock = canEditOthers;
  const canResetPassword = canEdit || isSelf;

  return { canEdit, canEditSelf, canDeleteOrLock, canResetPassword };
};

export const getUserContextMenuItems = (
  params: GetContextMenuItemsParams,
  currentUserRoles: Role[],
  currentUsername: string,
): (DefaultMenuItem | MenuItemDef)[] => {
  const rowData = params.node?.data as User;
  if (!rowData?.email) return [];

  const defaultItems = params.defaultItems ?? [];

  // Calculate permissions for the target user
  const permissions = getUserPermissions(currentUserRoles, rowData.roles ?? [], currentUsername, rowData.email);

  const customItems: MenuItemDef[] = [
    {
      name: 'Edit',
      //TODO: pass actions when we'll have the modals implemented
      icon: '✏️',
      disabled: !permissions.canEdit,
    },
    {
      name: 'Lock/Unlock',
      //TODO: pass actions when we'll have the modals implemented
      disabled: !permissions.canDeleteOrLock,
      icon: rowData.locked ? '🔓' : '🔒',
    },
    {
      name: 'Reset Password',
      //TODO: pass actions when we'll have the modals implemented
      disabled: !permissions.canResetPassword,
      icon: '🔑',
    },
    {
      name: 'Delete',
      //TODO: pass actions when we'll have the modals implemented
      disabled: !permissions.canDeleteOrLock,
      icon: '🗑️',
    },
  ];

  return [...defaultItems, 'separator', ...customItems];
};

export function getFieldErrorTestId(errorMsg?: unknown): string | undefined {
  let msg: string | undefined;
  if (typeof errorMsg === 'string') {
    msg = errorMsg;
  } else if (typeof errorMsg === 'object' && errorMsg !== null && 'message' in errorMsg) {
    msg = String(errorMsg.message);
  } else {
    msg = undefined;
  }
  if (!msg) return undefined;
  return `user-management-create-user-error-${msg.replaceAll(' ', '-').toLowerCase()}`;
}
